import React, { ReactNode, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { ActivityIndicator, Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import TimePicker from 'react-time-picker';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import moment from 'moment';
import MyDatePicker from '../../../components/MyDatePicker';
import Divider from '../../../components/Divider';
import FloatingButton from '../../../components/FloatingButton';
import Header from '../../../components/Header';
import CustomButton from '../../../components/CustomButton';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { RootNavigation } from '../../../Routes';
import { RouteProp, useRoute } from '@react-navigation/native';
import { AppAgentParamList } from '../../../typings/Params';
import Container from '../../../components/Container';
import { makeSetStateHandler } from '../../../components/helpers/form';
import ActivitiesAndServicesLocationSelector from './ActivitiesAndServicesLocationSelector';
import PatrolActivityDropdown from '../../../components/app-agent/PatrolActivityDropdown';
import IncrementDecrementInput from '../../../components/IncrementDecrementInput';
import TakePicture from './TakePicture';
import { MyImage } from '../../../components/MyImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OccurrenceList from '../../../components/Filters/App-Agent/OccurrenceList';
import DeletePolicePatrolScheduleModal from './DeletePolicePatrolScheduleModal';
import { computeEffectiveRange, RasItem, usePolicePatrolScheduleDetails } from './PolicePatrolScheduleDetailsHook';
import { faPlus, faTrash, faX } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { appAgentService } from '../../../services/central-api/app-agent';
import ActivitiesAndServicesDetails from './ActivitiesAndServicesDetails';

interface SectionHeaderParams {
    title: string;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
    required?: boolean;
}

function SectionHeader({ children, title, viewZIndex, childrenZIndex, required }: SectionHeaderParams) {
    const { styles, theme } = useStyles(styleSheet);

    return (
        <View style={[styles.sectionHeaderContainer, { zIndex: viewZIndex }]}>
            <View style={[styles.sectionHeaderTitleWrapper, { zIndex: viewZIndex ?? 2 }]}>
                <View style={styles.sectionHeaderRow}>
                    <View style={styles.sectionHeaderTitleBox}>
                        <MyAppText style={styles.sectionHeaderTitle}>{translate(title)}</MyAppText>
                        {required && <MyAppText style={styles.requiredStar}>(*)</MyAppText>}
                    </View>
                </View>
                <Divider style={styles.divider} color={theme.colors.primary['200']} />
            </View>
            <View style={[styles.childrenContainer, { zIndex: childrenZIndex ?? 1 }]}>
                {children}
            </View>
        </View>
    );
}

interface ConfirmRemoveRasModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

function ConfirmRemoveRasModal({ visible, onClose, onConfirm }: ConfirmRemoveRasModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    return (
        <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
            <View style={styles.confirmModalContainer}>
                <View style={styles.confirmModalFormContainer(windowInfo.isMobile)}>
                    <MyAppText style={styles.confirmModalHeader}>{translate('attention')}</MyAppText>
                    <MyAppText style={styles.confirmModalSubheader}>
                        {translate('areYouSureYouWantToDeleteQuestion')}
                    </MyAppText>
                    <FormActions
                        viewStyle={{ flexDirection: 'row', justifyContent: 'space-around', width: '100%' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText="goBack"
                        confirmText="delete"
                        onSubmit={onConfirm}
                        onClose={onClose}
                        disabled={false}
                    />
                </View>
            </View>
        </Modal>
    );
}

export default function PolicePatrolScheduleDetails({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<AppAgentParamList, 'PolicePatrolScheduleDetails'>>();
    const activityId = route.params?.activityId;
    const suggestedOdometer = route.params?.odometer;
    const isFinalizing = !!route.params?.finalize;

    const { styles, theme } = useStyles(styleSheet);
    const isEditing = !!activityId;

    const {
        form,
        values,
        data,
        activeUnit,
        isLoading,
        sendingData,
        pin,
        setPin,
        datePickerZIndex,
        setDatePickerZIndex,
        isDeleteModalVisible,
        setIsDeleteModalVisible,
        isAttachmentRequired,
        setIsAttachmentRequired,
        settingCepInternallyRef,
        activityRange,
        activityRangeLabel,
        formSubmit,
        clearValues,
        navigateBackOrHome,
    } = usePolicePatrolScheduleDetails({ activityId, suggestedOdometer, isFinalizing, navigation });

    const { fields: rasFields, append: appendRas, remove: removeRas, update: updateRas } = useFieldArray({
        control: form.control,
        name: 'multipleRas',
    });

    const { fields: attachmentFields, append: appendAttachment, remove: removeAttachment } = useFieldArray({
        control: form.control,
        name: 'attachments',
    });

    const [editingRasIndex, setEditingRasIndex] = useState<number | null>(null);
    const [editingRasItem, setEditingRasItem] = useState<RasItem | undefined>(undefined);
    const [isRasModalOpen, setIsRasModalOpen] = useState<boolean>(false);

    function openRasModal(index?: number) {
        setEditingRasIndex(index ?? null);
        setEditingRasItem(index !== undefined ? form.getValues('multipleRas')[index] : undefined);
        setIsRasModalOpen(true);
    }

    function closeRasModal() {
        setIsRasModalOpen(false);
        setEditingRasIndex(null);
        setEditingRasItem(undefined);
    }

    const patrolIndicatorsQuery = useQuery({
        queryKey: ['getSimplifiedPatrolIndicators'],
        queryFn: () => appAgentService.getSimplifiedPatrolIndicators(),
    });
    const indicatorsById = (patrolIndicatorsQuery.data ?? []).reduce<Record<number, PatrolIndicator>>((acc, indicator) => {
        acc[indicator.id] = indicator;
        return acc;
    }, {});

    const [removingRasIndex, setRemovingRasIndex] = useState<number | null>(null);

    function confirmRemoveRas() {
        if (removingRasIndex !== null) {
            removeRas(removingRasIndex);
        }
        setRemovingRasIndex(null);
    }

    return (
        <Container>
            <View style={[styles.formBody, isLoading && styles.formBodyLoading]}>
                <Header
                    title={translate(isFinalizing ? 'finalizePolicePatrolSchedule' : 'policePatrolSchedule')}
                    goBackAction={() => {
                        clearValues();
                        navigateBackOrHome();
                    }}
                />

                <ScrollView
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[styles.scrollViewContent, isLoading && styles.scrollViewContentLoading]}
                >
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color={theme.colors.iconColor} />
                        </View>
                    ) : (
                        <>
                            <Controller
                                control={form.control}
                                name="activityId"
                                rules={{ validate: (value) => value !== 0 || translate('required_field') }}
                                render={({ field }) => (
                                    <PatrolActivityDropdown
                                        value={field.value}
                                        setValue={makeSetStateHandler(form, 'activityId')}
                                        zIndex={4}
                                        required
                                        labelStyle={styles.fieldsLabelStyle}
                                        viewStyle={styles.itemView}
                                        onValueChange={(activity) => setIsAttachmentRequired(activity.needAttachment)}
                                    />
                                )}
                            />

                            <SectionHeader title="localization" viewZIndex={3} childrenZIndex={3}>
                                <ActivitiesAndServicesLocationSelector
                                    form={form}
                                    lastCep={settingCepInternallyRef.current}
                                    setCep={(val) => (settingCepInternallyRef.current = val)}
                                    pin={pin}
                                    setPin={setPin}
                                    isEditing={isEditing}
                                />
                            </SectionHeader>

                            <SectionHeader
                                title="activityData"
                                viewZIndex={datePickerZIndex - 1}
                                childrenZIndex={datePickerZIndex}
                            >
                                <MyDatePicker
                                    form={form}
                                    dateFieldName="date"
                                    required
                                    label={translate('activityDate')}
                                    hintText={translate('activityDateHint')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                    minDate={activeUnit?.definitiveStart ? moment(activeUnit.definitiveStart).toDate() : undefined}
                                    maxDate={moment().toDate()}
                                    zIndex={2}
                                    onCalendarOpen={() => setDatePickerZIndex(99999)}
                                    onCalendarClose={() => setDatePickerZIndex(2)}
                                />

                                <View style={styles.timePickerRow}>
                                    <Controller
                                        control={form.control}
                                        name="startTime"
                                        rules={{ required: translate('required_field') }}
                                        render={({ field, fieldState }) => (
                                            <View style={styles.timePickerCol}>
                                                <View style={styles.timePickerLabelRow}>
                                                    <MyAppText style={styles.fieldsLabelStyle}>{translate('startTime')}</MyAppText>
                                                    <MyAppText style={styles.requiredStar}>(*)</MyAppText>
                                                </View>
                                                <TimePicker
                                                    disableClock
                                                    format="HH:mm"
                                                    locale="pt-BR"
                                                    maxDetail="minute"
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', 'inputColorLight'] : ['inputColor', 'inputColorDark']}
                                                    value={field.value}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            field.onChange(value);
                                                            form.trigger('endTime');
                                                        }
                                                    }}
                                                />
                                                {fieldState.error && (
                                                    <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                                )}
                                            </View>
                                        )}
                                    />

                                    {isEditing && (
                                        <Controller
                                            control={form.control}
                                            name="endTime"
                                            rules={{
                                                required: (isFinalizing || !!values.endTime) ? translate('required_field') : undefined,
                                                validate: (value) => {
                                                    const startTime = form.getValues('startTime');
                                                    if (startTime && value && startTime === value) {
                                                        return translate('startEndTimeCannotBeEqual');
                                                    }
                                                    return true;
                                                },
                                            }}
                                            render={({ field, fieldState }) => (
                                                <View style={styles.timePickerCol}>
                                                    <View style={styles.timePickerLabelRow}>
                                                        <MyAppText style={styles.fieldsLabelStyle}>{translate('endTime')}</MyAppText>
                                                        {(isFinalizing || !!values.endTime) && (
                                                            <MyAppText style={styles.requiredStar}>(*)</MyAppText>
                                                        )}
                                                    </View>
                                                    <TimePicker
                                                        disableClock
                                                        format="HH:mm"
                                                        locale="pt-BR"
                                                        maxDetail="minute"
                                                        className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', 'inputColorLight'] : ['inputColor', 'inputColorDark']}
                                                        value={field.value}
                                                        clearIcon={null}
                                                        onChange={(value) => {
                                                            field.onChange(value ?? undefined);
                                                            form.trigger('startTime');
                                                        }}
                                                    />
                                                    {fieldState.error && (
                                                        <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                                    )}
                                                </View>
                                            )}
                                        />
                                    )}
                                </View>

                                {isEditing && activityRangeLabel && (
                                    <View style={styles.rangePreview}>
                                        <MyAppText style={styles.rangePreviewText}>{activityRangeLabel.rangeText}</MyAppText>
                                        <MyAppText style={styles.rangePreviewDuration}>
                                            {translate('duration')}: {activityRangeLabel.durationText}
                                        </MyAppText>
                                        {activityRangeLabel.crossesMidnight && !activityRangeLabel.isSameValue && (
                                            <MyAppText style={styles.rangePreviewWarning}>
                                                {translate('activityCrossesMidnightWarning')}
                                            </MyAppText>
                                        )}
                                    </View>
                                )}
                            </SectionHeader>

                            {isEditing && (
                                <>
                                    <SectionHeader title="finalizationData" required={!!values.endTime}>
                                        {suggestedOdometer ? (
                                            <IncrementDecrementInput
                                                value={form.getValues('odometer') || 0}
                                                setValue={makeSetStateHandler(form, 'odometer')}
                                                label={translate('odometer')}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView}
                                                required={!!values.endTime}
                                            />
                                        ) : (
                                            <FormInput
                                                form={form}
                                                fieldName="odometer"
                                                required={!!values.endTime}
                                                customValidations={{
                                                    valid: (value) => !value || value > 0 || translate('invalid_field'),
                                                }}
                                                label={translate('odometer')}
                                                mode="NUMBER"
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView}
                                            />
                                        )}
                                    </SectionHeader>

                                    <SectionHeader
                                        title="attachments"
                                        required={isAttachmentRequired && !!values.endTime}
                                    >
                                        {attachmentFields.length > 0 && (
                                            <View style={styles.attachmentsListContainer}>
                                                {attachmentFields.map((fieldItem, index) => (
                                                    <View key={fieldItem.id} style={styles.attachmentCard}>
                                                        <View style={styles.attachmentCardHeader}>
                                                            <MyAppText style={styles.attachmentCardTitle}>
                                                                {`${translate('attachment')} #${index + 1}`}
                                                            </MyAppText>
                                                            <TouchableOpacity
                                                                onPress={() => removeAttachment(index)}
                                                                style={styles.removeAttachmentBtn}
                                                            >
                                                                <FontAwesomeIcon icon={faX} fontSize={16} color={theme.colors.errors['300']} />
                                                            </TouchableOpacity>
                                                        </View>

                                                        <View style={styles.attachmentImageWrapper}>
                                                            <MyImage style={styles.attachmentImage} imageUrl={fieldItem.imageUrl} />
                                                        </View>

                                                        <FormInput
                                                            form={form}
                                                            fieldName={`attachments.${index}.description`}
                                                            label={translate('attachmentDescription')}
                                                            labelStyle={styles.fieldsLabelStyle}
                                                            viewStyle={styles.itemView}
                                                        />
                                                    </View>
                                                ))}
                                            </View>
                                        )}
                                        <TakePicture
                                            onConfirm={(attachment) => appendAttachment(attachment)}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title="observations">
                                        <FormInput
                                            form={form}
                                            fieldName="observations"
                                            label={translate('observations')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title="activityAndService">
                                        {rasFields.length > 0 && (
                                            <View style={styles.rasSummaryContainer}>
                                                {rasFields.map((fieldItem, index) => {
                                                    const indicator = indicatorsById[fieldItem.indicatorId];

                                                    const rasRange = activityRange
                                                        ? computeEffectiveRange(fieldItem.startTime, fieldItem.endTime, undefined, activityRange.start.toDate())
                                                        : null;

                                                    const period = rasRange?.end
                                                        ? `${rasRange.start.format('DD/MM HH:mm')} → ${rasRange.end.format('DD/MM HH:mm')}`
                                                        : `${fieldItem.startTime} → ${fieldItem.endTime}`;

                                                    const address = fieldItem.useParentAddress ? values.address : fieldItem.address;
                                                    const number = fieldItem.useParentAddress ? values.number : fieldItem.number;
                                                    const location = [address, number].filter(Boolean).join(', ') || translate('otherAddress');

                                                    return (
                                                        <TouchableOpacity
                                                            key={fieldItem.id}
                                                            style={styles.rasSummaryRow}
                                                            onPress={() => openRasModal(index)}
                                                            activeOpacity={0.7}
                                                        >
                                                            <View style={styles.rasSummaryMain}>
                                                                <View style={styles.rasSummaryHeader}>
                                                                    <MyAppText style={styles.rasSummaryTitle} numberOfLines={1}>
                                                                        {indicator ? `${indicator.code} · ${indicator.name}` : '—'}
                                                                    </MyAppText>
                                                                    <MyAppText style={styles.rasSummaryIndex}>{`#${index + 1}`}</MyAppText>
                                                                </View>
                                                                <MyAppText style={styles.rasSummaryMeta} numberOfLines={1}>
                                                                    {period}
                                                                    {' · '}
                                                                    {location}
                                                                </MyAppText>
                                                            </View>
                                                            <TouchableOpacity onPress={() => setRemovingRasIndex(index)} style={styles.removeRasBtn}>
                                                                <FontAwesomeIcon icon={faX} fontSize={16} color={theme.colors.errors['300']} />
                                                            </TouchableOpacity>
                                                        </TouchableOpacity>
                                                    );
                                                })}
                                            </View>
                                        )}

                                        <CustomButton
                                            text={translate('add')}
                                            type="tertiary"
                                            icon={faPlus}
                                            onPress={() => openRasModal()}
                                        />
                                    </SectionHeader>

                                    <ActivitiesAndServicesDetails
                                        visible={isRasModalOpen}
                                        initialValue={editingRasItem}
                                        rdpAddress={{
                                            cep: values.cep ?? '',
                                            address: values.address ?? '',
                                            number: values.number,
                                            latitude: values.latitude ?? '',
                                            longitude: values.longitude ?? '',
                                        }}
                                        rdpRange={activityRange}
                                        onClose={closeRasModal}
                                        onSave={(item) => {
                                            if (editingRasIndex !== null) {
                                                updateRas(editingRasIndex, item);
                                            } else {
                                                appendRas(item);
                                            }
                                            closeRasModal();
                                        }}
                                    />

                                    <ConfirmRemoveRasModal
                                        visible={removingRasIndex !== null}
                                        onClose={() => setRemovingRasIndex(null)}
                                        onConfirm={confirmRemoveRas}
                                    />
                                </>
                            )}
                            <SectionHeader title="occurrence" viewZIndex={2} childrenZIndex={3}>
                                <OccurrenceList
                                    value={values.occurrenceIds!}
                                    setValue={makeSetStateHandler(form, 'occurrenceIds')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                />
                            </SectionHeader>
                        </>
                    )}
                </ScrollView>
            </View>

            <View style={styles.footer}>
                <View style={styles.footerView}>
                    <CustomButton
                        contentContainerStyle={styles.footerButtonContainer}
                        style={styles.footerButton}
                        text={translate(!activityId ? 'save' : 'saveChanges')}
                        type="primary"
                        onPress={formSubmit}
                        disabled={isLoading || !form.formState.isValid}
                        isLoading={isLoading || sendingData}
                    />
                </View>
            </View>

            <FloatingButton
                visible={!!activityId && !isLoading}
                actions={[
                    {
                        name: 'test_delete',
                        label: translate('deletePolicePatrolScheduleAcronym'),
                        color: '#A14C4C',
                        icon: faTrash,
                        onPress: () => setIsDeleteModalVisible(true),
                    },
                ]}
                offset={{ vertical: 120 }}
                draggable
            />

            {data && (
                <DeletePolicePatrolScheduleModal
                    isModalVisible={isDeleteModalVisible}
                    setModalVisible={setIsDeleteModalVisible}
                    selectedPolicePatrol={data}
                    onConfirm={() => {
                        setIsDeleteModalVisible(false);
                        clearValues();
                        navigateBackOrHome();
                    }}
                />
            )}
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formBody: {
        backgroundColor: theme.colors.card.background,
        padding: 16,
        flex: 1,
        gap: 16,
    },
    formBodyLoading: {
        height: '100%',
    },
    scrollViewContent: {
        flexDirection: 'column',
        gap: 24,
        marginBottom: 104,
    },
    scrollViewContentLoading: {
        flex: 1,
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    smallFieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 13,
    },
    itemView: {
        gap: 8,
        flex: 1,
    },
    loadingContainer: {
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10,
    },
    requiredStar: {
        color: theme.colors.errors['300'],
    },
    rangePreview: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 4,
    },
    rangePreviewText: {
        color: theme.colors.neutral['500'],
        fontSize: 14,
        fontWeight: '600',
    },
    rangePreviewDuration: {
        color: theme.colors.neutral['400'],
        fontSize: 12,
    },
    rangePreviewWarning: {
        color: theme.colors.warnings['300'],
        fontSize: 11,
        fontWeight: '600',
    },
    sectionHeaderContainer: {
        flexDirection: 'column',
        rowGap: 16,
    },
    sectionHeaderTitleWrapper: {
        flexDirection: 'column',
        rowGap: 8,
    },
    sectionHeaderRow: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
    },
    sectionHeaderTitleBox: {
        flexDirection: 'row',
        gap: 3,
        alignContent: 'center',
        alignItems: 'baseline',
    },
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    divider: {
        zIndex: -1,
    },
    childrenContainer: {
        gap: 8,
    },
    timePickerRow: {
        flexDirection: 'row',
        gap: 12,
    },
    timePickerCol: {
        flexDirection: 'column',
        gap: 8,
        flex: 1,
    },
    timePickerLabelRow: {
        flexDirection: 'row',
        columnGap: 5,
    },
    attachmentContainer: {
        position: 'relative',
    },
    attachmentImage: {
        width: 240,
        height: 120,
        resizeMode: 'contain',
    },
    deleteAttachmentBtn: {
        position: 'absolute',
        padding: 10,
        top: 0,
        left: 245,
        borderWidth: 1,
        borderRadius: 999,
        borderColor: theme.colors.errors['300'],
    },
    rasSummaryContainer: {
        flexDirection: 'column',
        gap: 8,
        marginBottom: 8,
    },
    rasSummaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        paddingVertical: 10,
        paddingHorizontal: 12,
        gap: 8,
    },
    rasSummaryMain: {
        flex: 1,
        gap: 4,
    },
    rasSummaryHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rasSummaryTitle: {
        flex: 1,
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    rasSummaryIndex: {
        color: theme.colors.neutral['400'],
        fontWeight: '600',
        fontSize: 12,
    },
    rasSummaryMeta: {
        color: theme.colors.neutral['400'],
        fontSize: 12,
    },
    removeRasBtn: {
        padding: 6,
    },
    confirmModalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    confirmModalFormContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        alignItems: 'center',
    }),
    confirmModalHeader: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    confirmModalSubheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
        textAlign: 'center',
    },
    footer: {
        position: 'absolute',
        bottom: 0,
        height: 104,
        width: '100%',
        backgroundColor: theme.colors.white['500'],
    },
    footerView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 24,
        paddingHorizontal: 16,
    },
    footerButtonContainer: {
        width: '100%',
        height: '100%',
        minHeight: 56,
    },
    footerButton: {
        minHeight: 56,
    },
    attachmentsListContainer: {
        flexDirection: 'column',
        gap: 16,
        marginBottom: 8,
    },
    attachmentCard: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        padding: 12,
        gap: 8,
    },
    attachmentCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    attachmentCardTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    removeAttachmentBtn: {
        padding: 4,
    },
    attachmentImageWrapper: {
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        backgroundColor: theme.colors.field.background,
    },
}));
